import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "../lib/supabase";
import { admin, ApiError } from "../lib/api";

// Alta de ferretería: crea el usuario en Supabase Auth y luego aprovisiona el
// tenant (org+sucursal+caja+plan free+owner) vía la Edge Function signup, que
// además inyecta el claim {organization_id, role} en el JWT.
export const Route = createFileRoute("/registro")({ component: Registro });

function Registro() {
  const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "", fullName: "", tradeName: "", legalName: "", ruc: "", dv: "" });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      // 1. Crear usuario en Auth.
      const { data: signUp, error: e1 } = await supabase.auth.signUp({ email: f.email, password: f.password });
      if (e1) throw new Error(e1.message);
      const uid = signUp.user?.id;
      if (!uid) throw new Error("No se pudo crear el usuario.");

      // 2. Aprovisionar el tenant (EF service_role).
      await admin.signup({
        authUserId: uid, email: f.email, fullName: f.fullName,
        legalName: f.legalName || f.tradeName, tradeName: f.tradeName, ruc: f.ruc, dv: f.dv,
      });

      // 3. Refrescar sesión para recoger el claim de tenant y entrar.
      await supabase.auth.refreshSession();
      nav({ to: "/" });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-grafito-900 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
        <div className="mb-1 text-center text-2xl font-extrabold">🔧 FerreSaaS</div>
        <p className="mb-6 text-center text-sm text-grafito-400">Crea tu ferretería</p>
        {err && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}

        <Field label="Nombre comercial" v={f.tradeName} on={set("tradeName")} placeholder="Ferretería El Tornillo" />
        <Field label="Razón social" v={f.legalName} on={set("legalName")} placeholder="El Tornillo, S.A. (opcional)" />
        <div className="grid grid-cols-[1fr_90px] gap-3">
          <Field label="RUC" v={f.ruc} on={set("ruc")} placeholder="155600000-2-2021" />
          <Field label="DV" v={f.dv} on={set("dv")} placeholder="86" />
        </div>
        <Field label="Tu nombre" v={f.fullName} on={set("fullName")} placeholder="Rubén Him" />
        <Field label="Correo" type="email" v={f.email} on={set("email")} />
        <Field label="Contraseña" type="password" v={f.password} on={set("password")} />

        <button disabled={busy} className="btn btn-primary mt-2 w-full py-2.5 disabled:opacity-50">
          {busy ? "Creando…" : "Crear ferretería"}
        </button>
        <p className="mt-4 text-center text-sm text-grafito-400">
          ¿Ya tienes cuenta? <Link to="/login" className="text-brand">Entrar</Link>
        </p>
      </form>
    </div>
  );
}

function Field({ label, v, on, type = "text", placeholder }: {
  label: string; v: string; on: (e: React.ChangeEvent<HTMLInputElement>) => void; type?: string; placeholder?: string;
}) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <input type={type} value={v} onChange={on} placeholder={placeholder} required={type !== "text" || label !== "Razón social"}
        className="w-full rounded-lg border px-3 py-2 outline-none focus:border-brand" />
    </div>
  );
}
